import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export const SUPPORTED_LOCALES = ["en", "pl"];
export const DEFAULT_LOCALE = "pl";

const translations = {
  en: {
    language: "Language",
    english: "English",
    polish: "Polski",
    nav: {
      home: "Home",
      browse: "Browse",
      matches: "Matches",
      messages: "Messages",
      register: "Register",
      login: "Login",
      buyUnits: "Buy units",
      balance: "Balance",
      units: "Tokens",
      profile: "My Profile",
      paymentHistory: "Payment history",
      logout: "Logout"
    },
    home: {
      badge: "TajneMysli Private Chat",
      title: "Meet someone who sparks your interest",
      welcome: "Welcome back, {username}",
      subtitle: "Browse active profiles, create connections, and enjoy easy conversations on a modern communication platform.",
      interestMusic: "Music",
      interestTravel: "Travel",
      interestMovies: "Movies",
      browse: "Browse Users",
      viewMatches: "View Matches",
      createAccount: "Create Account",
      newMatch: "New match",
      findSpark: "Find your spark",
      previewDescription: "Coffee enthusiast · Weekend walks · Good conversations",
      tokenBalance: "Token balance",
      messageCost: "Each message costs 20 units.",
      discover: "Discover",
      discoverDescription: "Browse users and create new matches.",
      matchesDescription: "See people who liked you back.",
      profileDescription: "Keep your profile attractive and up to date.",
      howItWorks: "How TajneMysli works",
      browseStep: "Browse",
      browseStepDescription: "Find people who match your interests.",
      matchStep: "Create a match",
      matchStepDescription: "Like someone and connect instantly.",
      chatStep: "Start chatting",
      chatStepDescription: "Send messages and build a connection.",
      meetPeopleAlt: "Meet real people",
      memberAlt: "Member {number}"
    },
    auth: {
      welcomeBack: "Welcome back",
      loginDescription: "Login to continue browsing profiles, viewing your matches, and chatting with people who sparked your interest.",
      continueDiscovering: "Continue discovering people",
      latestMatches: "View your latest matches",
      tokenMessages: "Send messages",
      login: "Login",
      loginDescriptionShort: "Enter your email and password to access your account.",
      email: "Email",
      password: "Password",
      enterEmail: "Enter email",
      enterPassword: "Enter password",
      showPassword: "Show password",
      hidePassword: "Hide password",
      loggingIn: "Logging in...",
      forgotPassword: "Forgot your password?",
      noAccount: "Do not have an account?",
      createAccount: "Create account",
      loginFailed: "Login failed. Could not connect to backend."
    },
    registration: {
      createAccount: "Create your account",
      description: "Join TajneMysli, complete your profile, discover interesting people, and start making meaningful matches.",
      profileStep: "Create your dating profile",
      browseStep: "Browse people near you",
      matchStep: "Match and start chatting",
      register: "Register",
      rulesDescription: "Create your profile step by step and confirm the service rules.",
      welcome: "Welcome",
      welcomeTitle: "Welcome to TajneMysli",
      welcomeDescription: "Create your profile in less than a minute and start discovering meaningful matches.",
      account: "Account",
      profile: "Profile",
      consent: "Consent",
      energyTitle: "Find people who match your energy.",
      energyDescription: "We will guide you through a few quick steps: account details, profile basics, and consent confirmation.",
      quickSetup: "Quick profile setup",
      ageSafe: "Age-safe registration",
      clearConsent: "Clear service consent",
      getStarted: "Get Started",
      createAccountTitle: "Create your account",
      createAccountDescription: "Add your login details. You will use them to access your profile.",
      tellAboutYourself: "Tell us about yourself",
      profileDescription: "This information helps other people understand who you are.",
      confirmRules: "Confirm the rules",
      confirmRulesDescription: "Please review and accept the service rules before creating your account.",
      stepOf: "Step {step} of {total}",
      showcaseCaption: "Meet people like them",
      login: "Login",
      email: "Email",
      enterEmail: "Enter email",
      password: "Password",
      passwordHint: "At least 6 characters",
      nickname: "Nickname",
      enterNickname: "Enter nickname",
      age: "Age",
      selectAge: "Select age",
      gender: "Gender",
      selectGender: "Select gender",
      female: "Female",
      male: "Male",
      other: "Other",
      country: "Country",
      city: "City",
      selectCity: "Select city",
      bio: "Bio",
      bioPlaceholder: "Write something about yourself",
      back: "Back",
      continue: "Continue",
      checkingEmail: "Checking email...",
      agreeTo: "I agree to the",
      terms: "Terms of Service",
      serviceRules: "Service Rules",
      privacyPolicy: "Privacy Policy",
      and: "and",
      confirmAdult: "I confirm that I am 18 years old or older.",
      consentNote: "Your acceptance timestamp and the current terms version will be securely recorded by the server.",
      creatingAccount: "Creating account...",
      alreadyHaveAccount: "Already have an account?",
      loginInstead: "Login instead",
      validationEmailRequired: "Email is required.",
      validationEmail: "Please enter a valid email address (e.g. yourname@mail.com).",
      validationPasswordRequired: "Password is required.",
      validationPasswordLength: "Password must be at least {length} characters.",
      validationNickname: "Nickname is required.",
      validationAge: "Age must be between 18 and 120.",
      validationGender: "Gender is required.",
      validationCountry: "Country is required.",
      validationCity: "City is required.",
      validationBio: "Bio must be {length} characters or less.",
      validationTerms: "You must accept the Terms of Service and Privacy Policy.",
      validationAdult: "You must confirm that you are 18 years old or older.",
      emailExists: "This email is already registered. Please log in or use a different email.",
      registrationFailed: "Registration failed. Could not connect to backend.",
      accountCreated: "Account created successfully. Please log in.",
      cancelRegistration: "Cancel registration"
    },
    activity: {
      title: "Activity",
      empty: "No activity yet.",
      online: "Online",
      offline: "Offline",
      justNow: "just now",
      minutesAgo: "{count} min ago",
      hoursAgo: "{count} h ago",
      types: {
        VISIT: "visited your profile",
        MATCH: "liked you — it's a match!",
        ONLINE: "is online — write quickly!",
        MESSAGE: "sent you a message"
      }
    },
    outOfTokens: {
      title: "You're out of tokens",
      text: "Top up your balance to keep chatting. Choose a pack:",
      tokens: "Tokens",
      free: "free",
      buy: "Buy",
      buyGeneric: "Buy tokens",
      close: "Close"
    },
    messages: {
      title: "Messages",
      outOfTokens: "You run out of tokens, please top-up the balance.",
      all: "All",
      unread: "Unread",
      awaitingReply: "Awaiting reply",
      loading: "Loading conversations...",
      empty: "No conversations here yet.",
      viewMatches: "View Matches",
      chooseConversation: "Choose a conversation to read it.",
      startConversation: "Say hello and start the conversation.",
      send: "Send",
      sending: "Sending...",
      back: "Back to Messages",
      newMessages: "New messages",
      hide: "Hide conversation"
    },
    matches: {
      title: "My matches",
      subtitle: "People who liked you back. Start a conversation.",
      sendMessage: "Send Message"
    },
    profilePreview: {
      viewProfile: "View profile",
      close: "Close profile",
      unknownUser: "Unknown user",
      online: "Online",
      offline: "Offline",
      age: "Age",
      gender: "Gender",
      location: "Location",
      bio: "About",
      notProvided: "Not provided",
      bioMissing: "This user has not shared anything about themselves yet.",
      readMore: "Read more"
    },
    support: {
      title: "Help & Support",
      description: "Have an issue? Let us know and we'll help you out as soon as possible.",
      issueType: "Issue Type",
      contentIssue: "Content Issue",
      paymentIssue: "Payment Issue",
      accountIssue: "Account Issue",
      bugReport: "Bug Report",
      abuseReport: "Abuse Report",
      other: "Other",
      describe: "Describe your issue",
      placeholder: "Please describe what happened...",
      attach: "Attach file (optional)",
      close: "Close",
      submit: "Submit Ticket",
      sending: "Sending...",
      cancel: "Cancel",
      fileSize: "File size must be less than 10MB",
      selectType: "Please select a ticket type.",
      enterMessage: "Please enter a message.",
      success: "Your {type} ticket #{id} was created successfully. Support team will review it soon.",
      sessionExpired: "Session expired. Please login again.",
      createError: "Could not create support ticket. Please try again."
    },
    browse: {
      title: "Browse Users",
      loading: "Loading users...",
      finding: "Finding people for you...",
      subtitle: "Discover people and create instant matches.",
      filterHeading: "Refine your search",
      filterHint: "Start broad, then narrow down your matches.",
      buyUnits: "Buy Units",
      onlineNow: "Online now",
      country: "Country",
      anyCountry: "Any country",
      city: "City",
      searchCity: "Search city",
      gender: "Gender",
      anyGender: "Any gender",
      female: "Female",
      male: "Male",
      other: "Other",
      minAge: "Min age",
      maxAge: "Max age",
      applyFilters: "Apply filters",
      reset: "Reset",
      resultsPerPage: "Results per page",
      usersFound: "{count} users found",
      resultsPage: "Page {page}",
      noUsers: "No users available",
      noMoreUsers: "There are no more users to browse right now.",
      goToMatches: "Go to Matches",
      previous: "Previous",
      next: "Next",
      profileDetailsMissing: "Profile details not provided",
      like: "♥ Like / Create Match",
      viewVideo: "View video",
      online: "Online",
      offline: "Offline",
      matchCreated: "Match created.",
      matchCreatedWithChat: "It's a match! You can now chat in Matches.",
      dailyLimitTitle: "Daily match limit reached",
      dailyLimitMessage: "You have reached your daily match limit. Please try again tomorrow.",
      close: "Close"
    },
    profile: {
      title: "My Profile",
      subtitle: "Keep your details up to date so people can find you.",
      loading: "Loading profile...",
      photoPreview: "Profile Photo Preview",
      videoPreview: "Profile Video Preview",
      accountDetails: "Account Details",
      username: "Username",
      email: "Email",
      emailLocked: "Email cannot be changed.",
      age: "Age",
      selectAge: "Select age",
      gender: "Gender",
      notSet: "Not set",
      genderLocked: "Gender is set at registration and cannot be changed.",
      country: "Country",
      city: "City",
      photoUrl: "Photo URL",
      videoUrl: "Video URL",
      bio: "Bio",
      save: "Save Profile",
      saving: "Saving...",
      examplePoland: "Example: Poland",
      exampleWarsaw: "Example: Warsaw",
      profileExamplePhoto: "https://picsum.photos/300",
      profileExampleVideo: "https://example.com/video.mp4",
      updated: "Profile updated successfully."
    },
    footer: {
      description: "A modern platform for conversations, discovering interesting profiles, and engaging chats.",
      contact: "Contact us",
      help: "Help",
      getHelp: "Get help",
      social: "Social",
      legal: "Legal",
      terms: "Terms of Service",
      rules: "Service Rules",
      about: "About",
      cookies: "Cookies",
      copyright: "TajneMysli App. All rights reserved."
    },
    errorBoundary: {
      title: "Something went wrong",
      message: "We couldn't load this page. Please try again.",
      reload: "Reload page"
    }
  },
  pl: {
    language: "Język",
    english: "English",
    polish: "Polski",
    nav: {
      home: "Strona główna",
      browse: "Przeglądaj",
      matches: "Dopasowania",
      messages: "Wiadomości",
      register: "Rejestracja",
      login: "Logowanie",
      buyUnits: "Kup Tokeny",
      balance: "Saldo",
      units: "Tokeny",
      profile: "Mój profil",
      paymentHistory: "Historia płatności",
      logout: "Wyloguj"
    },
    home: {
      badge: "TajneMysli Private Chat",
      title: "Poznaj kogoś, kto wzbudzi Twoje zainteresowanie",
      welcome: "Witaj ponownie, {username}",
      subtitle: "Przeglądaj aktywne profile, twórz interakcje i prowadź swobodne rozmowy na nowoczesnej platformie komunikacyjnej",
      interestMusic: "Muzyka",
      interestTravel: "Podróże",
      interestMovies: "Filmy",
      browse: "Przeglądaj osoby",
      viewMatches: "Zobacz dopasowania",
      createAccount: "Utwórz konto",
      newMatch: "Nowe dopasowanie",
      findSpark: "Znajdź swoją iskrę",
      previewDescription: "Miłośnik kawy · Weekendowe spacery · Dobre rozmowy",
      tokenBalance: "Saldo tokenów",
      messageCost: "Każda wiadomość kosztuje 20 tokenów.",
      discover: "Odkrywaj",
      discoverDescription: "Przeglądaj osoby i twórz nowe dopasowania.",
      matchesDescription: "Zobacz osoby, które odwzajemniły zainteresowanie.",
      profileDescription: "Dbaj o atrakcyjność i aktualność swojego profilu.",
      howItWorks: "Jak działa TajneMysli",
      browseStep: "Przeglądaj",
      browseStepDescription: "Znajdź osoby o podobnych zainteresowaniach.",
      matchStep: "Utwórz dopasowanie",
      matchStepDescription: "Polub kogoś i połączcie się od razu.",
      chatStep: "Zacznij rozmawiać",
      chatStepDescription: "Wysyłaj wiadomości i buduj relację.",
      meetPeopleAlt: "Poznaj prawdziwe osoby",
      memberAlt: "Użytkownik {number}"
    },
    auth: {
      welcomeBack: "Witaj ponownie",
      loginDescription: "Zaloguj się, aby przeglądać profile, oglądać dopasowania i rozmawiać z osobami, które Cię zainteresowały.",
      continueDiscovering: "Odkrywaj kolejne osoby",
      latestMatches: "Zobacz najnowsze dopasowania",
      tokenMessages: "Wysyłaj wiadomości",
      login: "Logowanie",
      loginDescriptionShort: "Podaj e-mail i hasło, aby uzyskać dostęp do konta.",
      email: "E-mail",
      password: "Hasło",
      enterEmail: "Wpisz e-mail",
      enterPassword: "Wpisz hasło",
      showPassword: "Pokaż hasło",
      hidePassword: "Ukryj hasło",
      loggingIn: "Logowanie...",
      forgotPassword: "Nie pamiętasz hasła?",
      noAccount: "Nie masz konta?",
      createAccount: "Utwórz konto",
      loginFailed: "Logowanie nie powiodło się. Nie można połączyć się z serwerem."
    },
    registration: {
      createAccount: "Utwórz konto",
      description: "Dołącz do TajneMysli, uzupełnij profil, odkrywaj interesujące osoby i twórz wartościowe dopasowania.",
      profileStep: "Utwórz swój profil randkowy",
      browseStep: "Przeglądaj osoby w pobliżu",
      matchStep: "Dopasuj się i zacznij rozmawiać",
      register: "Rejestracja",
      rulesDescription: "Utwórz profil krok po kroku i potwierdź zasady serwisu.",
      welcome: "Witamy",
      welcomeTitle: "Witamy w TajneMysli",
      welcomeDescription: "Utwórz profil w mniej niż minutę i zacznij odkrywać wartościowe dopasowania.",
      account: "Konto",
      profile: "Profil",
      consent: "Zgoda",
      energyTitle: "Znajdź osoby, które pasują do Twojej energii.",
      energyDescription: "Przeprowadzimy Cię przez kilka szybkich kroków: dane konta, podstawowe informacje o profilu i potwierdzenie zgód.",
      quickSetup: "Szybkie tworzenie profilu",
      ageSafe: "Rejestracja dla osób pełnoletnich",
      clearConsent: "Jasne zasady i zgody",
      getStarted: "Rozpocznij",
      createAccountTitle: "Utwórz konto",
      createAccountDescription: "Dodaj dane logowania, których użyjesz, aby uzyskać dostęp do swojego profilu.",
      tellAboutYourself: "Opowiedz nam o sobie",
      profileDescription: "Te informacje pomogą innym osobom lepiej Cię poznać.",
      confirmRules: "Potwierdź zasady",
      confirmRulesDescription: "Przeczytaj i zaakceptuj zasady serwisu przed utworzeniem konta.",
      stepOf: "Krok {step} z {total}",
      showcaseCaption: "Poznaj osoby takie jak one",
      login: "Logowanie",
      alreadyHaveAccount: "Masz już konto?",
      loginInstead: "Zaloguj się",
      email: "E-mail",
      enterEmail: "Wpisz e-mail",
      password: "Hasło",
      passwordHint: "Minimum 6 znaków",
      nickname: "Pseudonim",
      enterNickname: "Wpisz pseudonim",
      age: "Wiek",
      selectAge: "Wybierz wiek",
      gender: "Płeć",
      selectGender: "Wybierz płeć",
      female: "Kobieta",
      male: "Mężczyzna",
      other: "Inna",
      country: "Kraj",
      city: "Miasto",
      selectCity: "Wybierz miasto",
      bio: "Opis",
      bioPlaceholder: "Napisz coś o sobie",
      back: "Wstecz",
      continue: "Dalej",
      checkingEmail: "Sprawdzanie e-maila...",
      agreeTo: "Akceptuję",
      terms: "Regulamin",
      serviceRules: "Zasady serwisu",
      privacyPolicy: "Politykę prywatności",
      and: "oraz",
      confirmAdult: "Potwierdzam, że mam co najmniej 18 lat.",
      consentNote: "Data akceptacji i aktualna wersja zasad zostaną bezpiecznie zapisane przez serwer.",
      creatingAccount: "Tworzenie konta...",
      validationEmailRequired: "E-mail jest wymagany.",
      validationEmail: "Wpisz prawidłowy adres e-mail (np. twojanazwa@mail.com).",
      validationPasswordRequired: "Hasło jest wymagane.",
      validationPasswordLength: "Hasło musi mieć co najmniej {length} znaków.",
      validationNickname: "Pseudonim jest wymagany.",
      validationAge: "Wiek musi wynosić od 18 do 120 lat.",
      validationGender: "Płeć jest wymagana.",
      validationCountry: "Kraj jest wymagany.",
      validationCity: "Miasto jest wymagane.",
      validationBio: "Opis może mieć maksymalnie {length} znaków.",
      validationTerms: "Musisz zaakceptować Regulamin i Politykę prywatności.",
      validationAdult: "Musisz potwierdzić, że masz co najmniej 18 lat.",
      emailExists: "Ten e-mail jest już zarejestrowany. Zaloguj się lub użyj innego adresu.",
      registrationFailed: "Rejestracja nie powiodła się. Nie można połączyć się z serwerem.",
      accountCreated: "Konto zostało utworzone. Zaloguj się.",
      cancelRegistration: "Anuluj rejestrację"
    },
    activity: {
      title: "Aktywność",
      empty: "Brak aktywności.",
      online: "Online",
      offline: "Offline",
      justNow: "przed chwilą",
      minutesAgo: "{count} min temu",
      hoursAgo: "{count} godz. temu",
      types: {
        VISIT: "odwiedziła Twój profil",
        MATCH: "polubiła Cię — macie match!",
        ONLINE: "jest online — napisz szybko!",
        MESSAGE: "wysłała Ci wiadomość"
      }
    },
    outOfTokens: {
      title: "Skończyły Ci się tokeny",
      text: "Doładuj saldo, aby kontynuować rozmowę. Wybierz pakiet:",
      tokens: "Tokenów",
      free: "gratis",
      buy: "Kup",
      buyGeneric: "Kup tokeny",
      close: "Zamknij"
    },
    messages: {
      title: "Wiadomości",
      outOfTokens: "Skończyły Ci się tokeny. Doładuj saldo, aby kontynuować.",
      all: "Wszystkie",
      unread: "Nieprzeczytane",
      awaitingReply: "Oczekujące na odpowiedź",
      loading: "Ładowanie rozmów...",
      empty: "Nie masz jeszcze rozmów.",
      viewMatches: "Zobacz dopasowania",
      chooseConversation: "Wybierz rozmowę, aby ją przeczytać.",
      startConversation: "Przywitaj się i rozpocznij rozmowę.",
      send: "Wyślij",
      sending: "Wysyłanie...",
      back: "Powrót do wiadomości",
      newMessages: "Nowe wiadomości",
      hide: "Ukryj rozmowę"
    },
    matches: {
      title: "Moje dopasowania",
      subtitle: "Osoby, które polubiły Cię z wzajemnością. Rozpocznij rozmowę.",
      sendMessage: "Wyślij wiadomość"
    },
    profilePreview: {
      viewProfile: "Zobacz profil",
      close: "Zamknij profil",
      unknownUser: "Nieznany użytkownik",
      online: "Online",
      offline: "Offline",
      age: "Wiek",
      gender: "Płeć",
      location: "Lokalizacja",
      bio: "O mnie",
      notProvided: "Brak danych",
      bioMissing: "Ten użytkownik nie udostępnił jeszcze informacji o sobie.",
      readMore: "Czytaj więcej"
    },
    support: {
      title: "Pomoc i wsparcie",
      description: "Masz problem? Napisz nam o nim, a pomożemy Ci tak szybko, jak to możliwe.",
      issueType: "Rodzaj zgłoszenia",
      contentIssue: "Problem z treścią",
      paymentIssue: "Problem z płatnością",
      accountIssue: "Problem z kontem",
      bugReport: "Zgłoszenie błędu",
      abuseReport: "Zgłoszenie nadużycia",
      other: "Inne",
      describe: "Opisz problem",
      placeholder: "Opisz, co się wydarzyło...",
      attach: "Załącz plik (opcjonalnie)",
      close: "Zamknij",
      submit: "Wyślij zgłoszenie",
      sending: "Wysyłanie...",
      cancel: "Anuluj",
      fileSize: "Rozmiar pliku musi być mniejszy niż 10 MB",
      selectType: "Wybierz rodzaj zgłoszenia.",
      enterMessage: "Wpisz wiadomość.",
      success: "Zgłoszenie typu „{type}” nr {id} zostało utworzone. Zespół wsparcia wkrótce je rozpatrzy.",
      sessionExpired: "Sesja wygasła. Zaloguj się ponownie.",
      createError: "Nie udało się utworzyć zgłoszenia. Spróbuj ponownie."
    },
    browse: {
      title: "Przeglądaj osoby",
      loading: "Ładowanie osób...",
      finding: "Szukamy dla Ciebie osób...",
      subtitle: "Odkrywaj osoby i twórz natychmiastowe dopasowania.",
      filterHeading: "Dopasuj wyszukiwanie",
      filterHint: "Zacznij szeroko, a następnie zawęź dopasowania.",
      buyUnits: "Kup Tokeny",
      onlineNow: "Teraz online",
      country: "Kraj",
      anyCountry: "Dowolny kraj",
      city: "Miasto",
      searchCity: "Szukaj miasta",
      gender: "Płeć",
      anyGender: "Dowolna",
      female: "Kobieta",
      male: "Mężczyzna",
      other: "Inna",
      minAge: "Wiek od",
      maxAge: "Wiek do",
      applyFilters: "Zastosuj filtry",
      reset: "Wyczyść",
      resultsPerPage: "Wyników na stronę",
      usersFound: "Znaleziono osób: {count}",
      resultsPage: "Strona {page}",
      noUsers: "Brak dostępnych osób",
      noMoreUsers: "Obecnie nie ma więcej osób do przeglądania.",
      goToMatches: "Przejdź do dopasowań",
      previous: "Poprzednia",
      next: "Następna",
      profileDetailsMissing: "Brak szczegółów profilu",
      like: "♥ Polub / utwórz dopasowanie",
      viewVideo: "Zobacz wideo",
      online: "Online",
      offline: "Offline",
      matchCreated: "Dopasowanie utworzone.",
      matchCreatedWithChat: "To dopasowanie! Możesz teraz rozmawiać w sekcji Dopasowania.",
      dailyLimitTitle: "Osiągnięto dzienny limit dopasowań",
      dailyLimitMessage: "Osiągnięto dzienny limit dopasowań. Spróbuj ponownie jutro.",
      close: "Zamknij"
    },
    profile: {
      title: "Mój profil",
      subtitle: "Aktualizuj swoje dane, aby inni mogli Cię znaleźć.",
      loading: "Ładowanie profilu...",
      photoPreview: "Podgląd zdjęcia profilu",
      videoPreview: "Podgląd wideo profilu",
      accountDetails: "Dane konta",
      username: "Nazwa użytkownika",
      email: "E-mail",
      emailLocked: "Adresu e-mail nie można zmienić.",
      age: "Wiek",
      selectAge: "Wybierz wiek",
      gender: "Płeć",
      notSet: "Nie ustawiono",
      genderLocked: "Płeć jest ustawiana podczas rejestracji i nie można jej zmienić.",
      country: "Kraj",
      city: "Miasto",
      photoUrl: "Adres URL zdjęcia",
      videoUrl: "Adres URL wideo",
      bio: "Opis",
      save: "Zapisz profil",
      saving: "Zapisywanie...",
      examplePoland: "Przykład: Polska",
      exampleWarsaw: "Przykład: Warszawa",
      profileExamplePhoto: "https://picsum.photos/300",
      profileExampleVideo: "https://example.com/video.mp4",
      updated: "Profil został zaktualizowany."
    },
    footer: {
      description: "Nowoczesna platforma do rozmów, odkrywania ciekawych profili i prowadzenia wciągających rozmów",
      contact: "Kontakt",
      help: "Pomoc",
      getHelp: "Uzyskaj pomoc",
      social: "Media społecznościowe",
      legal: "Informacje prawne",
      terms: "Regulamin",
      rules: "Zasady serwisu",
      about: "O nas",
      cookies: "Pliki cookie",
      copyright: "TajneMysli App. Wszelkie prawa zastrzeżone."
    },
    errorBoundary: {
      title: "Coś poszło nie tak",
      message: "Nie udało się załadować tej strony. Spróbuj ponownie.",
      reload: "Odśwież stronę"
    }
  }
};

function getNestedValue(source, key) {
  return key.split(".").reduce((value, part) => value?.[part], source);
}

function interpolate(value, variables) {
  return value.replace(/\{(\w+)\}/g, (_, name) => String(variables?.[name] ?? `{${name}}`));
}

export function translate(locale, key, variables) {
  const value = getNestedValue(translations[locale], key) ?? getNestedValue(translations[DEFAULT_LOCALE], key);
  return typeof value === "string" ? interpolate(value, variables) : key;
}

const fallbackContext = {
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key, variables) => translate(DEFAULT_LOCALE, key, variables)
};

const I18nContext = createContext(fallbackContext);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    const savedLocale = window.localStorage.getItem("locale");
    return SUPPORTED_LOCALES.includes(savedLocale) ? savedLocale : DEFAULT_LOCALE;
  });

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  function setLocale(nextLocale) {
    const safeLocale = SUPPORTED_LOCALES.includes(nextLocale) ? nextLocale : DEFAULT_LOCALE;
    window.localStorage.setItem("locale", safeLocale);
    setLocaleState(safeLocale);
  }

  const value = useMemo(() => ({
    locale,
    setLocale,
    t: (key, variables) => translate(locale, key, variables)
  }), [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export { translations };
